import { parseURL, isSamePath, withoutLeadingSlash } from 'ufo';
import type { RouteLocationGeneric } from '#vue-router';

export const STATIC_MERCHANT_SLUG = 'c';

export const useMerchantUrlUtils = () => {
	const { public: { appBaseUrl, hasCustomUrlsEnabled, staticMerchantUrls } } = useRuntimeConfig();

	const MERCHANT_SLUG_TO_URL_MAP = staticMerchantUrls as Record<string, string>;

	const isStaticMerchantUrl = (url: string): boolean => {
		if (!hasCustomUrlsEnabled) return false;
		const host = parseURL(url).host;
		const result = Object.entries(MERCHANT_SLUG_TO_URL_MAP).find(([,u]) => parseURL(u).host === host);
		return !!result;
	};

	const hasMerchantStaticUrl = (merchantSlug: string): boolean => {
		if (!hasCustomUrlsEnabled) return false;
		return merchantSlug in MERCHANT_SLUG_TO_URL_MAP && !!MERCHANT_SLUG_TO_URL_MAP[merchantSlug];
	};

	const getStaticMerchantUrl = (merchantSlug: string): string | null => {
		if (!hasMerchantStaticUrl(merchantSlug)) {
			return null;
		}

		return MERCHANT_SLUG_TO_URL_MAP[merchantSlug] as string;
	};

	const isOnCorrectMerchantUrl = (merchantSlug: string, url: string): boolean => {
		if (!hasCustomUrlsEnabled) {
			return true;
		}

		if (!hasMerchantStaticUrl(merchantSlug)) {
			return parseURL(appBaseUrl).host === parseURL(url).host;
		}

		const staticMerchantUrl = MERCHANT_SLUG_TO_URL_MAP[merchantSlug];

		return parseURL(staticMerchantUrl).host === parseURL(url).host;
	};

	const getMerchantUrl = (merchantSlug: string): string => {
		const staticMerchantUrl = getStaticMerchantUrl(merchantSlug);
		return staticMerchantUrl || appBaseUrl;
	};

	const hasStaticMerchantSlug = (url: string) => {
		const [firstPathName] = withoutLeadingSlash(parseURL(url).pathname).split('/');
		return firstPathName && isSamePath(firstPathName, STATIC_MERCHANT_SLUG);
	};

	const getMerchantSlugByUrl = (url: string): string | null => {
		if (!hasCustomUrlsEnabled) {
			return null;
		}

		const host = parseURL(url).host;

		if (!host) {
			return null;
		}

		const result = Object.entries(MERCHANT_SLUG_TO_URL_MAP).find(([,u]) => parseURL(u).host === host);

		if (!result) {
			return null;
		}

		return result[0];
	};

	const router = useRouter();

	const getRouteWithMerchantSlug = (route: RouteLocationGeneric, merchantSlug: string) => {
		// @ts-expect-error We want to explicitly append a merchantSlug to a route which could of course not be valid for typescript
		return router.resolve({ ...route, params: { ...route.params, merchantSlug } });
	};

	const getRouteWithStaticSlug = (route: RouteLocationGeneric) => {
		return getRouteWithMerchantSlug(route, STATIC_MERCHANT_SLUG);
	};

	const getMerchantSlugMaybeStatic = (merchantSlug: string) => {
		if (merchantSlug !== STATIC_MERCHANT_SLUG && hasMerchantStaticUrl(merchantSlug)) {
			return STATIC_MERCHANT_SLUG;
		}

		return merchantSlug;
	};

	return {
		isStaticMerchantUrl,
		hasMerchantStaticUrl,
		getStaticMerchantUrl,
		isOnCorrectMerchantUrl,
		hasStaticMerchantSlug,
		getMerchantSlugByUrl,
		getRouteWithMerchantSlug,
		getRouteWithStaticSlug,
		getMerchantUrl,
		getMerchantSlugMaybeStatic,
	};
};
